import React, { Key, useMemo } from 'react';
import {
  DataTableProps,
  DataTableColumnsType,
  DataTableColumnType,
  DataTableRenderExpandedRow,
  DataTableRowsType,
  DataTableRowType,
} from './types';
import { TableRow } from './TableRow';
import { css } from '@emotion/react';
import { Checkbox } from '../checkbox';
import { Stack } from '../stack';
import { TableCell } from './TableCell';
import { getFixedMergeColumn } from './utils';
import { IconButton } from '../button';
import { motion } from 'framer-motion';
import { IconArrowChevronDown } from '../icons/generated';
import { semantic_colors } from '../../foundation';
import styled from '@emotion/styled';
import { COLUMN_MIN_WIDTH, TABLE_CONTROL_CELL_HORIZONTAL_PADDING, TABLE_EXPAND_ICON_SIZE } from './constants';

const ArrowMotion = motion(Stack);

export interface DataTableRowProps<DataType extends object>
  extends Pick<
    DataTableProps<DataType>,
    'selectableRows' | 'clickableRows' | 'hasCellHoverStyle' | 'hasCellDisabledStyle' | 'resizable'
  > {
  row: DataTableRowType<DataType>;
  columns: DataTableColumnsType<DataType>;
  index: number;
  rows: DataTableRowsType<DataType>;
  hasExpandButton: boolean;
  renderExpandedRow?: DataTableRenderExpandedRow<DataType>;
  shouldRenderRowDivider?: (row: DataType, index: number) => boolean;
  selected: boolean;
  expanded: boolean;
  rowColor?: string;
  lastRow?: boolean;
  selectionKey?: Key;
  onSelectedRow?: (row: DataTableRowType<DataType>, index: number, selected: boolean) => void;
  onExpandedRow?: (row: DataTableRowType<DataType>, index: number, expanded: boolean) => void;
}

const DataTableRowImpl = <DataType extends object>({
  row,
  columns,
  index,
  rows,
  selected,
  expanded,
  rowColor,
  lastRow,
  selectionKey,
  onSelectedRow,
  onExpandedRow,
  selectableRows,
  hasExpandButton,
  renderExpandedRow,
  shouldRenderRowDivider,
  clickableRows,
  hasCellHoverStyle,
  hasCellDisabledStyle,
  resizable,
}: DataTableRowProps<DataType>) => {
  const hasSelectableRows = !!selectableRows;
  const expandedRow = renderExpandedRow && renderExpandedRow(row as DataType, index);
  const renderedColumns = useMemo(
    () =>
      columns.map((column, flattenIndex) => {
        const baseColumn = column as DataTableColumnType<DataType>;
        const { id, cellAttributes, wrapper = true, ...rest } = baseColumn;
        const align = baseColumn.cellAlign ?? baseColumn.align ?? 'left';
        const cellAttributesProps = cellAttributes && cellAttributes(row, index, rows);
        const { style, disabled: cellDisabled, ...spanProps } = cellAttributesProps || {};
        const tableCellProps = {
          resizable,
          ...spanProps,
          hasDisabledStyle: hasCellDisabledStyle,
          selectable: !row.disabled && (row.selectable ?? true),
          expandable: !row.disabled && (row.expandable ?? true),
          clickable: clickableRows,
          ...rest,
          width: undefined,
          ...(rest.fixed && {
            fixedColumnWidth: getFixedMergeColumn(
              columns,
              hasSelectableRows,
              flattenIndex,
              !!renderExpandedRow && hasExpandButton,
            ),
          }),
          disabled: cellDisabled !== undefined ? cellDisabled : hasCellDisabledStyle && row.disabled,
          align,
        };
        const render_cell = (
          baseColumn.cell ? baseColumn.cell({ row, value: row[id], index, rows }) : row[id]
        ) as React.ReactNode;
        return (
          <TableCell
            className={`pds-table-cell-${id}`}
            key={flattenIndex}
            {...tableCellProps}
            css={style}
            lastRow={
              Boolean(
                tableCellProps.rowSpan && tableCellProps.rowSpan > 1 && index + tableCellProps.rowSpan === rows.length,
              ) || lastRow
            }
            lastColumn={Boolean(
              tableCellProps.colSpan &&
                tableCellProps.colSpan > 1 &&
                flattenIndex + tableCellProps.colSpan === columns.length,
            )}
          >
            {wrapper ? (
              <Stack
                direction='row'
                align='center'
                style={{
                  justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'initial',
                  wordWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
              >
                {resizable ? <span className='ContentsWrapper'>{render_cell}</span> : render_cell}
              </Stack>
            ) : resizable ? (
              <span className='ContentsWrapper'>{render_cell}</span>
            ) : (
              render_cell
            )}
          </TableCell>
        );
      }),
    [
      columns,
      index,
      rows,
      hasCellDisabledStyle,
      row.disabled,
      row.selectable,
      clickableRows,
      hasSelectableRows,
      row.expandable,
    ],
  );

  const checkboxCell = useMemo(() => {
    if (!selectableRows) {
      return null;
    }
    const selectableRowsObject = typeof selectableRows === 'object' ? selectableRows : {};
    const cellAttributesProps = selectableRowsObject.cellAttributes?.(row, index, rows);
    const { style, ...spanProps } = cellAttributesProps || {};
    const checkboxCellProps = {
      selectable: !row.disabled && (row.selectable ?? true),
      clickable: clickableRows,
      align: 'center' as const,
      valign: 'middle' as const,
      ...(columns[0]?.fixed && { fixed: true, fixedColumnWidth: 0 }),
      ...spanProps,
    };
    return (
      <TableCell
        {...checkboxCellProps}
        css={[
          css`
            padding-right: ${TABLE_CONTROL_CELL_HORIZONTAL_PADDING}px;
            padding-left: ${TABLE_CONTROL_CELL_HORIZONTAL_PADDING}px;
          `,
          style,
        ]}
        lastRow={
          Boolean(
            checkboxCellProps.rowSpan &&
              checkboxCellProps.rowSpan > 1 &&
              index + checkboxCellProps.rowSpan === rows.length,
          ) || lastRow
        }
      >
        <Stack direction='row' align='center' justify='center'>
          <Checkbox
            name={`row-${selectionKey}-select`}
            disabled={row.disabled || !(row.selectable ?? true) || !selectionKey}
            checked={selected}
            aria-checked={selected}
            onChange={clickableRows ? undefined : () => onSelectedRow?.(row, index, selected)}
            readOnly={clickableRows ? true : undefined}
            onClick={(e) => e.stopPropagation()}
            css={css`
              justify-content: center;
            `}
          />
        </Stack>
      </TableCell>
    );
  }, [selectableRows, row, index, rows, clickableRows, columns, lastRow, selectionKey, selected, onSelectedRow]);

  const expandCell = useMemo(() => {
    if (!renderExpandedRow || !hasExpandButton) {
      return null;
    }

    const is_disabled = row.disabled || !(row.expandable ?? true);

    const expandCellProps = {
      expandable: !is_disabled,
      align: 'center' as const,
      valign: 'middle' as const,
      ...(columns[0]?.fixed && { fixed: true, fixedColumnWidth: selectableRows ? COLUMN_MIN_WIDTH : 0 }),
    };

    return (
      <TableCell {...expandCellProps}>
        <Stack direction='row' align='center' justify='center'>
          <IconButton
            variant='outlined'
            color={expanded ? 'primary' : 'secondary'}
            disabled={is_disabled || !selectionKey}
            size='xsmall'
            onClick={() => onExpandedRow?.(row, index, expanded)}
            icon={
              <ArrowMotion
                animate={{
                  rotate: expanded ? -180 : 0,
                }}
                transition={{ duration: 0.25 }}
              >
                <IconArrowChevronDown
                  size={TABLE_EXPAND_ICON_SIZE}
                  color={
                    is_disabled
                      ? semantic_colors.content.secondary
                      : expanded
                      ? semantic_colors.accent.primary
                      : semantic_colors.content.primary
                  }
                />
              </ArrowMotion>
            }
          />
        </Stack>
      </TableCell>
    );
  }, [renderExpandedRow, hasExpandButton, row, index, columns, selectableRows, expanded, selectionKey, onExpandedRow]);

  return (
    <>
      <TableRow
        selected={selected}
        rowColor={rowColor}
        hasHoverStyle={hasCellHoverStyle}
        onClick={!row.disabled && clickableRows ? () => onSelectedRow?.(row, index, selected) : undefined}
        selectable={!row.disabled && (row.selectable ?? true)}
        disabled={row.disabled}
        clickable={clickableRows}
      >
        {checkboxCell}
        {hasExpandButton && expandCell}
        {renderedColumns}
      </TableRow>
      {expandedRow && (!hasExpandButton || expanded) && (
        <TableRow>
          <TableCell
            className='pds-table-cell pds-table-expanded-cell'
            colSpan={columns.length + (selectableRows ? 1 : 0) + (hasExpandButton ? 1 : 0)}
          >
            <ExpandableRowContainer>{expandedRow}</ExpandableRowContainer>
          </TableCell>
        </TableRow>
      )}
      {rows.length - 1 !== index && shouldRenderRowDivider?.(row as DataType, index) && (
        <TableRow>
          <TableCell
            className='pds-table-cell pds-table-divider-cell'
            colSpan={columns.length + (selectableRows ? 1 : 0) + (!!renderExpandedRow && hasExpandButton ? 1 : 0)}
          />
        </TableRow>
      )}
    </>
  );
};

export const DataTableRow = React.memo(DataTableRowImpl) as typeof DataTableRowImpl;

const ExpandableRowContainer = styled.div`
  position: relative;
  width: 100%;
  z-index: 0;
`;
